/*
  KeyboardShortcutsHelp — global "?" overlay listing every keyboard
  shortcut supported across the app. Bound globally by Layout so it
  works from any page.

  Trigger: `?` (Shift+/) on any focus outside a text input/textarea/
  contenteditable — we don't want to swallow a legit `?` typed into a
  note. Also opens when the user clicks the "Shortcuts" pill in the
  header.

  Close: Esc, click backdrop, or click Done.

  Accessibility: role=dialog with aria-labelledby, focus ring on close
  button, all rows readable at 4.5:1 contrast (WCAG 2.2 AA).
*/
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

const SHORTCUTS = [
  {
    group: "Navigation",
    items: [
      { keys: ["⌘", "K"], mac: true,   desc: "Open global search (clients, enquiries, tasks, docs)" },
      { keys: ["Ctrl", "K"], mac: false, desc: "Open global search (Windows / Linux)" },
      { keys: ["?"],                     desc: "Open this shortcuts help" },
      { keys: ["Esc"],                   desc: "Close dialogs, panels, or overlays" },
    ],
  },
  {
    group: "Search palette",
    items: [
      { keys: ["↑"],                     desc: "Previous result" },
      { keys: ["↓"],                     desc: "Next result" },
      { keys: ["Enter"],                 desc: "Open the selected result" },
    ],
  },
  {
    group: "Editors",
    items: [
      { keys: ["Tab"],                   desc: "Move to next field or button" },
      { keys: ["Shift", "Tab"],          desc: "Move to previous field or button" },
      { keys: ["Enter"],                 desc: "Submit primary action in a form" },
    ],
  },
];

function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey
          && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    // Also let any part of the app dispatch a custom event to open the
    // overlay — e.g. a "Shortcuts" affordance in a menu.
    function onEvent() { setOpen(true); }
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-shortcuts-help", onEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-shortcuts-help", onEvent);
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-lg w-[92vw]"
        data-testid="shortcuts-help-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-indigo-700" />
            Keyboard shortcuts
          </DialogTitle>
          <DialogDescription className="text-xs">
            Everything you can do from the keyboard. Press <Kbd>?</Kbd> from anywhere
            (except while typing) to reopen this list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {SHORTCUTS.map((grp) => (
            <section key={grp.group} data-testid={`shortcuts-group-${grp.group.toLowerCase().replace(/\s+/g,"-")}`}>
              <div className="text-2xs uppercase tracking-wider font-semibold text-zinc-600 mb-1.5">
                {grp.group}
              </div>
              <ul className="space-y-1">
                {grp.items.map((it, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 text-sm text-zinc-800 rounded-md px-2 py-1.5 hover:bg-zinc-50"
                    data-testid={`shortcut-row-${grp.group.toLowerCase().replace(/\s+/g,"-")}-${i}`}
                  >
                    <span>{it.desc}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {it.keys.map((k, j) => (
                        <Kbd key={j}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="min-w-[24px] h-6 inline-flex items-center justify-center px-1.5 text-2xs font-mono-num font-semibold rounded border border-zinc-300 bg-white text-zinc-800 shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.06)]">
      {children}
    </kbd>
  );
}
